import { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { AlertTriangle, Pause, Play, History } from "lucide-react";
import { toast } from "sonner";

const MONTHS_OPTIONS = [1, 3, 6, 12, 24];
const POLL_MS = 2000;

// ---------------------------------------------------------------------------
// Admin-only long-run FullBay backfill launcher & progress dialog.
// Shows Start mode when no job exists / job is terminal.
// Shows Progress mode when a job is running or paused.
// ---------------------------------------------------------------------------
export default function BackfillHistoryDialog({ company, open, onOpenChange }) {
  const [job, setJob] = useState(null);
  const [months, setMonths] = useState(12);
  const [starting, setStarting] = useState(false);
  const [busy, setBusy] = useState(false);

  // Fetch latest job for this company whenever dialog opens.
  useEffect(() => {
    if (!open || !company) return;
    let cancelled = false;
    api.get("/equipment-repairs/backfill", { params: { company_id: company.id, limit: 1 } })
      .then((r) => {
        if (cancelled) return;
        const items = r.data.items || [];
        setJob(items[0] || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, company?.id]);

  // Poll while dialog open and job is active.
  useEffect(() => {
    if (!open || !job || !isActive(job.status)) return;
    let cancelled = false;
    const id = setInterval(() => {
      api.get(`/equipment-repairs/backfill/${job.id}`)
        .then((r) => {
          if (cancelled) return;
          setJob(r.data);
          if (r.data.status === "completed") {
            try { window.dispatchEvent(new Event("repairs:refresh-badge")); } catch { /* no-op */ }
          }
        })
        .catch(() => {});
    }, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [open, job?.id, job?.status]);

  const start = async () => {
    if (!company) return;
    setStarting(true);
    try {
      const { data } = await api.post("/equipment-repairs/backfill", {
        company_id: company.id, months_back: months,
      });
      setJob(data);
      toast.success(`Backfill started (${data.days_total} days)`);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setStarting(false);
    }
  };

  const pause = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/equipment-repairs/backfill/${job.id}/pause`);
      setJob(data);
      toast.success("Pausing… will stop after the current day finishes");
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const resume = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/equipment-repairs/backfill/${job.id}/resume`);
      setJob(data);
      toast.success("Resumed");
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const startNew = async () => {
    if (!job) return;
    setBusy(true);
    try {
      await api.delete(`/equipment-repairs/backfill/${job.id}`);
      setJob(null);
      toast.success("Previous job discarded — configure and start a new backfill");
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const inProgress = job && isActive(job.status);
  const pct = job && job.days_total > 0 ? Math.min(100, Math.round((job.days_completed / job.days_total) * 100)) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="backfill-history-dialog">
        <DialogHeader>
          <DialogTitle>Backfill FullBay history</DialogTitle>
          <DialogDescription>
            One-shot import of prior invoices for repeat-repair detection. Runs day-by-day
            in the background — you can close this dialog and check progress later.
          </DialogDescription>
        </DialogHeader>

        {company && (
          <div
            className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
            data-testid="backfill-company"
          >
            <span className="font-mono text-xs text-slate-500 mr-2">{company.code}</span>
            {company.fullbay_customer_title || <em className="text-slate-400">unmapped</em>}
          </div>
        )}

        {!inProgress && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Months back</Label>
              <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
                <SelectTrigger className="mt-1 h-9" data-testid="backfill-months"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS_OPTIONS.map((m) => (<SelectItem key={m} value={String(m)}>{m} month{m === 1 ? "" : "s"}</SelectItem>))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-slate-500">
                12 months backfill takes ~90 minutes at ~15s/day. You can pause and resume any time.
              </p>
            </div>
            {job && !isActive(job.status) && (
              <div className="rounded-md border border-slate-200 bg-white p-3 text-xs">
                <div className="mb-1 font-semibold text-slate-700">
                  Last job: <span className="font-mono">{job.status}</span>
                </div>
                <div className="text-slate-500">
                  {job.days_completed}/{job.days_total} days ·{" "}
                  {job.invoices_synced} invoices · {job.corrections_synced} corrections
                  {job.errors?.length ? ` · ${job.errors.length} errors` : ""}
                </div>
              </div>
            )}
          </div>
        )}

        {inProgress && (
          <div className="space-y-3" data-testid="backfill-progress">
            <div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">
                  {job.status === "paused" ? "Paused" : "Running"} · {job.days_completed}/{job.days_total} days
                </span>
                <span className="text-slate-500 text-xs">{pct}%</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded bg-slate-200">
                <div
                  className={`h-full transition-all ${job.status === "paused" ? "bg-slate-400" : "bg-emerald-500"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                cursor: <span className="font-mono">{job.cursor_date}</span> ·{" "}
                started {new Date(job.started_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <ProgressCell label="Invoices" value={job.invoices_synced} />
              <ProgressCell label="Corrections" value={job.corrections_synced} />
              <ProgressCell label="Errors" value={(job.errors || []).length} tone={(job.errors || []).length ? "danger" : "neutral"} />
            </div>
            {(job.errors || []).length > 0 && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-2">
                <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-rose-700">
                  <AlertTriangle className="h-3 w-3" /> Recent errors
                </div>
                <ul className="max-h-40 space-y-1 overflow-y-auto text-[11px] text-rose-800">
                  {(job.errors || []).slice(-20).reverse().map((e, i) => (
                    <li key={i}>
                      <span className="font-mono">{e.day}</span> — {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          {!inProgress && (
            <Button
              onClick={start}
              disabled={starting || !company?.fullbay_customer_title}
              className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
              data-testid="backfill-start"
            >
              <History className="mr-2 h-4 w-4" />
              {starting ? "Starting…" : "Start backfill"}
            </Button>
          )}
          {inProgress && job.status === "running" && (
            <Button onClick={pause} disabled={busy} variant="outline" data-testid="backfill-pause">
              <Pause className="mr-2 h-4 w-4" /> Pause
            </Button>
          )}
          {inProgress && job.status === "paused" && (
            <>
              <Button onClick={resume} disabled={busy} className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90" data-testid="backfill-resume">
                <Play className="mr-2 h-4 w-4" /> Resume
              </Button>
              <Button onClick={startNew} variant="ghost" size="sm" disabled={busy} data-testid="backfill-discard">
                Discard &amp; start new
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function isActive(status) {
  return status === "running" || status === "paused" || status === "queued";
}

function ProgressCell({ label, value, tone = "neutral" }) {
  const toneCls = tone === "danger" ? "text-rose-700" : "text-slate-900";
  return (
    <div className="rounded-md bg-slate-50 p-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}
